import React, { useState, useContext } from "react";
import { useMutation } from "@apollo/react-hooks";
import { compose, graphql } from "react-apollo";
import CreatableSelect from "react-select/creatable";
import { useDropzone } from "react-dropzone";
import ReactSwitch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUpload,
  faPlugCirclePlus,
  faFile
} from "@fortawesome/free-solid-svg-icons";
import { Query } from "react-apollo";

import { UploadDataContext } from "./UploadDataContext";
import {
  CREATE_DATA_SOURCE,
  PREPARE_UPLOAD_DATA
} from "../../../api/queries/uploadData.mutations";
import { getComptes } from "../../../api/queries/comptes.queries";
import { notify, formatNumber } from "../../generic";
import { splitTransaction } from "../../../api/queries/transactions.mutations";
import { useAuthState } from "../../Context";
import InputDialog from "./Modal/InputDialog";
import HorizontalStepper from "../../components/Interface/Stepper";

const fileToBase64 = async file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

const Dropzone = ({ handleFileChange }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileChange
  });

  return (
    <div
      {...getRootProps()}
      className={`dropzone ${isDragActive ? "drag-active" : ""}`}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Déposez les fichiers ici...</p>
      ) : (
        <>
          <FontAwesomeIcon icon={faCloudUpload} size="2x" />
          <p>
            <strong>Déposez votre fichier ici</strong>
          </p>
          <p>
            Fichiers acceptés : XLS, XLSX ou CSV d’une taille maximale de 15 Mo
          </p>
        </>
      )}
    </div>
  );
};

const UploadForm = ({
  onNextStep,
  PrepareUploadDataMutation,
  createDataSourceMutation
}) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [fileType, setFileType] = useState("");
  const [newDataSourceBalanceOpen, setNewDataSourceBalanceOpen] = useState(
    false
  );
  const [newDataSourceName, setNewDataSourceName] = useState("");
  const [fileBase64, setFileBase64] = useState(null);

  const authContext = useAuthState(); //r
  const { userDetails = {} } = authContext;
  const { id: utilisateurId, login, email, token } = userDetails;

  if (!utilisateurId) return null;

  const handleSwitchChange = checked => {
    setHasHeader(checked);
  };

  const {
    setData,
    nextStep,
    setJsonFile,
    dataSources,
    setDataSources,
    hasHeader,
    setHasHeader,
    selectedDataSource,
    setSelectedDataSource
  } = useContext(UploadDataContext);

  const _mutateCreateDataSource = ({
    newDataSourceName,
    newDataSourceBalance,
    refetch
  }) => {
    return createDataSourceMutation({
      variables: {
        dataSourceName: newDataSourceName,
        balance: parseFloat(newDataSourceBalance),
        utilisateurId
      },
      refetchQueries: [
        {
          query: getComptes,
          variables: {
            utilisateurId
          }
        }
      ]
    }).then(({ data }) => {
      const { type, message, details } = data.createDataSource;
      const { newSourceId = null } = JSON.parse(details);
      if (!newSourceId)
        throw new Error(
          "Erreur lors de la création de la nouvelle source de données"
        );
      setDataSources([
        ...dataSources,
        { label: newDataSourceName, value: newSourceId }
      ]);
    });
  };
  const CreatableDataSourceSelect = ({ options, value, onChange }) => {
    const handleChange = (newValue, actionMeta) => {
      if (actionMeta.action === "create-option") {
        // Vous pouvez ici traiter la création d'une nouvelle source de données
        // par exemple, enregistrez la nouvelle source de données dans la base de données
      }
      onChange(newValue);
    };

    const handleCreateOption = newSourceName => {
      try {
        if (!newSourceName || newSourceName.length < 3)
          throw new Error("Votre source de donnée doit avoir un nom plus long");

        if (
          options.filter(
            f =>
              String(f.label).toLowerCase() ===
              newSourceName.trim().toLowerCase()
          ).length
        )
          throw new Error(
            "La nouvelle source de donnée que vous insérez doit avoir un nom unique"
          );

        setNewDataSourceName(newSourceName);
        setNewDataSourceBalanceOpen(true);
      } catch (err) {
        notify("error", err.message);
      }
    };

    return (
      <Query query={getComptes} variables={{ utilisateurId }}>
        {({ loading, error, data = {}, refetch }) => {
          if (loading) return "--- €";
          if (error) return "--- €";

          const { getComptes = [] } = data;

          options = getComptes
            .filter(compte => compte.type === "datasource")
            .map(compte => {
              return {
                label: (
                  <span className="accountSelectLine">
                    {compte.name}
                    <strong>{formatNumber(compte.balance, "€")}</strong>
                  </span>
                ),
                value: compte.identifiant
              };
            });

          return (
            <CreatableSelect
              isClearable
              onCreateOption={handleCreateOption}
              onChange={handleChange}
              options={options}
              value={value}
              placeholder={
                "Veuillez sélectionner ou créer une source de données"
              }
              formatCreateLabel={inputValue =>
                `Créer la source de données "${inputValue}"`
              }
            />
          );
        }}
      </Query>
    );
  };

  const handleFileChange = async selectedFile => {
    if (selectedFile) {
      const { name, type, size } = selectedFile;
      let regex = /\..+$/;
      const fileExt = name.match(regex);

      const sizeInKB = size / 1024;
      if (sizeInKB < 1024) {
        // size is less than 1MB
        setFileSize(`${sizeInKB.toFixed(2)} Ko`);
      } else {
        // size is more than or equal to 1MB
        setFileSize(`${(sizeInKB / 1024).toFixed(2)} Mo`);
      }

      if (
        size > 15 * 1024 * 1024 ||
        ![".csv", ".xls", ".xlsx", "xls", "xlsx", "csv"].includes(fileExt[0])
      ) {
        alert(
          "Le fichier doit être un CSV, XLS, XLSX et peser moins de 15 Mo."
        );
        return;
      }
      setFile(selectedFile);
      setFileName(name);
      setFileType(fileExt[0]);
      const base64File = await fileToBase64(selectedFile);
      setFileBase64(base64File);
    }
  };

  const handleDataSourceChange = (option, { action }) => {
    if (action === "create-option") {
      setDataSources([...dataSources, option]);
    }
    setSelectedDataSource(option);
  };

  const handleCheckboxChange = event => {
    setHasHeader(event.target.checked);
  };

  const _mutatePrepareUpload = async (fb, hasHeader) => {
    try {
      return PrepareUploadDataMutation({
        variables: {
          base64File: fb,
          hasHeader
        }
      }).then(({ data }) => {
        const { jsonData } = data.prepareUploadData;

        if (!jsonData) throw new Error("Fichier non lisible");
        setJsonFile(jsonData);
        nextStep();
      });
    } catch (error) {
      notify("error", `Une erreur est survenue : ${error.message}`);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (fileBase64) {
      try {
        const { data } = await _mutatePrepareUpload(fileBase64, hasHeader);
        if (data.prepareUploadData.success) {
          setData(data.prepareUploadData.data);
          onNextStep();
        } else {
          alert(data.prepareUploadData.message);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    } else {
      alert("ko");
    }
  };

  return (
    <div className="col-md-6">
      <InputDialog
        title="Spécifiez le solde de la nouvelle source de données"
        label={`Quel est le solde actuel du compte ${newDataSourceName} ?`}
        open={newDataSourceBalanceOpen}
        setOpen={setNewDataSourceBalanceOpen}
        isNumeric
        onSubmitAction={({ value }) =>
          _mutateCreateDataSource({
            utilisateurId,
            newDataSourceBalance: value,
            newDataSourceName
          })
        }
      />

      <div>
        <h1 className="dashboardTitle">Sélection du fichier</h1>
        <p>
          Les imports manuels sont ajoutés dans des comptes différents de vos
          données dynamiques. Créez une nouvelle source de données ici, ou
          sélectionnez-en une existante.
          <br />
          <br />
          <strong>Important : </strong> si cette source de donnée existre déjà,
          le solde évoluera au gré de vos imports.
        </p>
        <label htmlFor="dataSource">Nom de compte</label>
        <CreatableDataSourceSelect
          options={dataSources}
          value={selectedDataSource}
          onChange={selectedOption => setSelectedDataSource(selectedOption)}
        />
      </div>

      <Dropzone
        handleFileChange={acceptedFiles => handleFileChange(acceptedFiles[0])}
      />

      {file && (
        <div className="fileStack">
          <div className="extension">
            <FontAwesomeIcon size="lg" icon={faFile} />
            <span className="ext"> {fileType}</span>
          </div>
          <p>
            <span className="fileName>">{fileName}</span>
            <br />
            <span className="fileSize">{fileSize}</span>
          </p>
          <div className="removeBtnZone">
            <a
              onClick={() => {
                setFile(null);
                setFileName("");
                setFileType("");
              }}
              className="btn btn-secondary btn-xs rounded-pill"
            >
              Supprimer le fichier
            </a>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="hasHeader">
          <input
            type="checkbox"
            id="hasHeader"
            checked={hasHeader}
            onChange={handleCheckboxChange}
            className="form-check-input"
            style={{ display: "none" }}
          />
          <label htmlFor="hasHeader">
            <ReactSwitch
              onChange={handleSwitchChange}
              checked={hasHeader}
              id="hasHeader"
              offColor="#767676"
              onColor="#3E4E3A"
              handleDiameter={30}
              uncheckedIcon={false}
              checkedIcon={false}
              height={20}
              width={48}
            />
          </label>
          <span className="switchLabel">Le fichier contient un en-tête</span>
        </label>
      </div>

      <button className="right" onClick={handleSubmit} disabled={!file}>
        Suivant
      </button>
    </div>
  );
};

export default compose(
  graphql(PREPARE_UPLOAD_DATA, {
    name: "PrepareUploadDataMutation"
  }),
  graphql(CREATE_DATA_SOURCE, {
    name: "createDataSourceMutation"
  })
)(UploadForm);
